import { ApiResponse } from 'apisauce';
import { ISearchResponse } from 'app/components/CommonStyled/Search/hooks/useSearch';
import {
  ICommunityConveningsParams,
  ICommunityConveningsResponse,
  ICommunityDataResponse,
  ICommunityResourcesParams,
  ICommunityResourcesResponse,
} from 'app/components/Communities/interfaces/community';
import {
  ICompanyDetailsResponse,
  ICompanyGlanceResponse,
  ICompanyImpactResponse,
  ICompanyNewPeopleResponse,
  ICompanyProjectsResponse,
  ICompanySnapshotResponse,
  ICompanyStoriesAndHighlightsResponse,
} from 'app/components/DetailsPages/interfaces/company';
import { IPersonGlobalDrawerResponse } from 'app/components/DetailsPages/interfaces/person';
import { ClientDrawerResponse } from 'app/components/Drawers/hooks/useClientDrawerData';
import { ICompanies, ICompanyListCompany, IFilters } from 'interfaces';

import { getHttpNoApiWithToken, getHttpWithToken, http, httpNoApi } from './http';
import { getToken } from './storage';

const CLIENT_ID = process.env.REACT_APP_CLIENT_ID;
const CLIENT_SECRET = process.env.REACT_APP_CLIENT_SECRET;

export * from './client';
export * from './collection';
export * from './community';
export * from './industry';
export * from './map';
export * from './passion';
export * from './person';
export * from './profile-editor';
export * from './project';
export * from './discipline';
export * from './skill';
export * from './the-well';
export * from './activity-feed';
export * from './onboarding';
export * from './resources';
export * from './people';
export * from './kyu101';
export * from './messages';
export * from './best-of-years';
export * from './prospect';
export * from './sme-councils';

export const getSearch = ({ term, model }) => {
  return http.get('/answer_options', { term, model });
};

export const getSearchHighlights = () => {
  return http.get('/search/highlights');
};

export const getSearchLenses = () => {
  return http.get('/search/lenses');
};

export const getSearchSingle = ({ term }: { term: string }) => {
  return http.get<Array<ISearchResponse>>('/search', { term });
};

export const getSearchSinglePeople = ({ term }: { term: string }) => {
  return http.get<Array<ISearchResponse>>('/search/people', { term });
};

export const getNLPSearch = (params: { query: string; request_origin?: string }) => {
  return http.get('/search/nlp', {
    prompt: params.query,
    request_origin: params.request_origin,
  });
};

export const updateNLPFeedback = (feedback, prompt_uuid) => {
  return http.put(`/search/update_nlp_feedback`, { feedback, prompt_uuid });
};

export const getResultsPageData = (uuidsByModel) => {
  return http.get(`/search/multiple${uuidsByModel}`);
};

export const getFavorites = () => {
  return http.get(`/favorite_cards`);
};

export const addFavorite = (card_type, card_uuids) => {
  return http.post(`/favorite_cards`, { card_type, card_uuids });
};

export const deleteFavorite = (card_uuid) => {
  return http.delete(`/favorite_cards`, { card_uuid });
};

export const getGraph = ({
  model,
  uuid,
  slug,
}: {
  model: string;
  uuid?: string;
  slug?: string;
}) => {
  return http.get('/graph', { model, uuid, slug });
};

export const getGraphMultiple = ({ skills, passions, companies }) => {
  return http.get('/people_passions_skills', {
    skills,
    passions,
    companies,
  });
};

export const getGraphPeople = ({ uuid, slug }: { uuid?: string; slug?: string[] }) => {
  return http.get('/known_group', { uuid, slug });
};

export const getKyuCompanies = (): Promise<ApiResponse<ICompanies>> => {
  return http.get('/kyu_companies');
};

export const sendFeedback = ({ consent, feedback_text }) => {
  return http.post('/mailer/feedbacks', { consent, feedback_text });
};

export const sendPulseFeedback = ({ subject, message }: { subject: string; message: string }) => {
  return http.post('/mailer/pulse_feedback', { subject, message });
};

export const sendBusinessDevelopmentContact = ({ consent, contact_text }) => {
  return http.post('/mailer/business_development', { consent, contact_text });
};

export const sendCommunitiesContact = ({ first_name, feedback_text }) => {
  return http.post('/mailer/communities', { first_name, feedback_text });
};

export const getProjectsList = ({ map_type, map_ids }) => {
  return http.get('/projects_list', { map_type, map_ids });
};

export const getPeopleList = ({ map_type, map_ids, page, per_page, alumni, sort, filters }) => {
  const props = {
    map_type: map_type,
    map_ids: map_ids,
    alumni: alumni,
    sort: sort,
    location: filters ? filters.location : [],
    kyu_companies: filters ? filters.kyu_companies : [],
    page: page,
    per_page: per_page,
  };

  return http.get('/people_list', props);
};

export const getNodeCardData = ({ map_type, map_ids, node_type, node_id }) => {
  return http.get('/node_cards', { map_type, map_ids, node_type, node_id });
};

export const getFilter = ({ map_type, map_ids, filter_type }) => {
  return http.get('/filters', { map_type, map_ids, filter_type });
};

export const getUserData = () => {
  return http.get(`/person/profile`);
};

export const updateOnboarded = (onboarded: boolean) => {
  return http.put(`/person/update_onboarded`, { onboarded });
};

export const getOnboardingCounters = () => {
  return http.get(`/onboarding/counters`);
};

export const getPersonDrawerData = (slug: string) => {
  return http.get<IPersonGlobalDrawerResponse>(`/person/${slug}/drawer_data`);
};

export const getSkillDrawerData = (slug: string) => {
  return http.get(`/skill/${slug}/drawer_data`);
};

export const getPassionDrawerData = (slug: string) => {
  return http.get(`/passion/${slug}/drawer_data`);
};

export const getCompanyDrawerData = (slug: string) => {
  return http.get(`/member_company/${slug}/drawer_data`);
};

export const getDisciplineDrawerData = (slug: string) => {
  return http.get(`/discipline/${slug}/drawer_data`);
};

export const getIndustryDrawerData = (slug: string) => {
  return http.get(`/industry/${slug}/drawer_data`);
};

export const getProjectDrawerData = (slug: string) => {
  return http.get(`/project/${slug}/drawer_data`);
};

export const getClientDrawerData = (slug: string) => {
  return http.get<ClientDrawerResponse>(`/client/${slug}/drawer_data`);
};

export const getCollectiveWithKyu = () => {
  return http.get(`/companies/collective_with_kyu`);
};

export const getDrawerData = (model: string, range?: string) => {
  return http.get<ICompanyListCompany[]>(
    `/search/options?model=${model}${range ? `&range=${range}` : ''}`
  );
};

export const getPeopleDropdownsData = () => {
  return http.get(`/search/option_filters?model=people`);
};

// REPORTS
export const getReports = () => {
  return http.get(`/reports`);
};

export const getReportsProfileCompletionOverview = (selectedOption) => {
  if (!selectedOption) {
    return http.get(`/reports/profile_completion_overview`);
  }
  const props = {
    report_id: selectedOption,
  };
  return http.get(`/reports/profile_completion_overview`, props);
};

export const getReportsProfileCompletionBreakdown = () => {
  return http.get(`/reports/profile_completion_breakdown`);
};

export const getReportsMasterUsage = () => {
  return http.get(`/reports/master_usage`);
};

export const getReportsTopKeywords = () => {
  return http.get(`/reports/top_keywords`);
};

export const getReportsActions = () => {
  return http.get(`/reports/actions`);
};

export const getReportsUsersOverview = () => {
  return http.get(`/reports/users_overview`);
};

export const getReportsUsersDisciplines = () => {
  return http.get(`/reports/users_disciplines`);
};

export const getReportsUsersLevels = () => {
  return http.get(`/reports/users_levels`);
};

export const getReportsArticles = () => {
  return http.get(`/reports/articles`);
};

export const getReportsCompanyTotals = () => {
  return http.get(`/reports/company_totals`);
};

export const getReportsTeamingOverview = () => {
  return http.get(`/reports/teaming_overview`);
};

export const getReportsBestOfOverview = () => {
  return http.get(`/reports/best_of_overview`);
};

export const getReportsBestOfCompanies = () => {
  return http.get(`/reports/best_of_companies`);
};

export const getReportsBestOfContributors = () => {
  return http.get(`/reports/best_of_contributors`);
};

export const getReportsBd100Overview = () => {
  return http.get(`/reports/bd100_overview`);
};

// COMMUNITIES
export const getCommunitiesIndexData = () => {
  return http.get(`/communities`);
};

export const getCommunityData = (slug: string) => {
  return http.get<ICommunityDataResponse>(`/communities/${slug}/details`);
};

export const getCommunityConvenings = (slug: string, params: ICommunityConveningsParams) => {
  return http.get<ICommunityConveningsResponse>(`/communities/${slug}/details/convenings`, params);
};

export const getUpcomingCommunityConvenings = (slug: string) => {
  return http.get<ICommunityConveningsResponse>(`/communities/${slug}/details/upcoming_convenings`);
};

export const getCommunityUpcomingConvenings = (
  slug: string,
  params: ICommunityConveningsParams
) => {
  return http.get<ICommunityConveningsResponse>(
    `/communities/${slug}/details/upcoming_convenings`,
    params
  );
};

export const getCommunityPastConvenings = (slug: string, params: ICommunityConveningsParams) => {
  return http.get<ICommunityConveningsResponse>(
    `/communities/${slug}/details/past_convenings`,
    params
  );
};

export const getCommunityResources = (slug: string, params: ICommunityResourcesParams) => {
  return http.get<ICommunityResourcesResponse>(`/communities/${slug}/details/resources`, params);
};

export const getConveningData = (slug: string) => {
  return http.get(`/convenings/${slug}`);
};

// AUTH

export const requestMagicLink = (email, redirect_to = '/') => {
  return http.post('/send_magic_link', { email, redirect_to });
};

export const exchangeMagicLink = (magicLinkToken: string) => {
  return http.post('/exchange_magic_link', { magic_link_token: magicLinkToken });
};

export const loginGoogleService = (user_token, redirect_to = '/') => {
  const dataToSend = {
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    grant_type: 'assertion',
    provider: 'google_oauth2',
    assertion: user_token,
    redirect_to,
  };

  return httpNoApi.post(`/oauth/token`, dataToSend);
};

export const loginMicrosoftService = (user_token, redirect_to = '/') => {
  const dataToSend = {
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    grant_type: 'assertion',
    provider: 'microsoft_oauth2',
    assertion: user_token,
    redirect_to,
  };

  return httpNoApi.post(`/oauth/token`, dataToSend);
};

export const logoutService = () => {
  const dataToSend = {
    client_id: CLIENT_ID,
    client_secret: CLIENT_SECRET,
    token: getToken(),
  };

  return httpNoApi.post(`/oauth/revoke`, dataToSend);
};

export const checkTokenStatus = (token: string) => {
  const httpNoApiWithToken = getHttpNoApiWithToken(token);
  return httpNoApiWithToken.get(`/oauth/token/info`);
};

export const updateTosStatus = (token: string) => {
  const httpWithToken = getHttpWithToken(token);
  return httpWithToken.put(`/accept_privacy_notice`);
};

export const setSignedIn = () => {
  return http.get(`/person/set_signed_in`);
};

export const getCompanyDetails = (slug: string) => {
  return http.get<ICompanyDetailsResponse>(`/member_company/${slug}/details`);
};

export const getCompanySnapshot = (slug: string) => {
  return http.get<ICompanySnapshotResponse>(`/member_company/${slug}/details/snapshot`);
};

export const getCompanyAtAGlance = (slug: string) => {
  return http.get<ICompanyGlanceResponse>(`/member_company/${slug}/details/at_a_glance`);
};

export const getCompanyImpact = (slug: string) => {
  return http.get<ICompanyImpactResponse>(`/member_company/${slug}/details/impact`);
};

export const getCompanyProjects = (slug: string) => {
  return http.get<ICompanyProjectsResponse>(`/member_company/${slug}/details/projects`);
};

export const getCompanyNewPeople = (slug: string) => {
  return http.get<ICompanyNewPeopleResponse>(`/member_company/${slug}/details/new_people`);
};

export const getCompanyStoriesAndHighlights = (slug: string) => {
  return http.get<ICompanyStoriesAndHighlightsResponse>(
    `/member_company/${slug}/details/stories_and_highlights`
  );
};

export const postDataHealthViewerEmails = (slug: string, viewers_emails: any[] = []) => {
  return http.post(`/member_company/${slug}/drawers/add_data_health_viewers`, { viewers_emails });
};

interface PeopleDrawerParams {
  passion_uuids?: string[];
  roles_uuids?: string[];
  locations_uuids?: string[];
  skills_uuids?: string[];
}

export const getPeopleDrawerList = (companySlug: string, params: PeopleDrawerParams) => {
  return http.get(`/member_company/${companySlug}/drawers/people`, params);
};

export const getSkillsDrawerList = (companySlug: string) => {
  return http.get(`/member_company/${companySlug}/drawers/skills`);
};

export const getPassionsDrawerList = (companySlug: string) => {
  return http.get(`/member_company/${companySlug}/drawers/passions`);
};

export const getIndustriesDrawerList = (companySlug: string) => {
  return http.get(`/member_company/${companySlug}/drawers/industries`);
};

export const getClientsDrawerList = (companySlug: string) => {
  return http.get(`/member_company/${companySlug}/drawers/clients`);
};

interface ProjectsDrawerParams {
  sort_dir?: string;
  filter_by?: string;
  filter_slugs_list?: string[];
  current_person?: boolean;
  page?: number;
  per_page?: number;
}

export const getProjectsDrawerList = (
  companySlug: string,
  params: ProjectsDrawerParams = {
    sort_dir: 'desc',
    filter_by: '',
    filter_slugs_list: [],
    current_person: false,
    page: 1,
    per_page: 50,
  }
) => {
  return http.get(`/member_company/${companySlug}/drawers/projects`, params);
};
export const sendContentRequest = (content) => {
  return http.post<any>(`/mailer/resource_request`, content);
};

export const getKyuTeam = () => {
  return http.get<any>('/kyu_team');
};

export const getMetaTags = (url: string) => {
  return http.get<any>('/meta_tags', { url });
};

export const getImageInfo = (url: string) => {
  return http.get<any>('/image_info', { url });
};
